import { SearchCriterion } from '@App/stores/search/models/search-criterion'
import SearchCriteriaStore from '@App/stores/search/search-criteria-store'
import { SearchOptionType } from '@App/enum/search-option-type'
import { RequiredCriteria } from '@App/enum/required-criteria'
import RootStore from '@App/stores/root-store'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { PaginationBody, PaginationInfo } from '@Entities/pagination'
import { isEqual } from 'lodash'
import { findPageOnLimitChange } from '@App/helpers/pagination/find-page-on-limit-change'
import { calcNumPages } from '@App/helpers/pagination/calc-num-pages'
import { OverclaimFilterCount } from '@Entities/overclaim/overclaim-status-count'
import { searchOverclaimsRH } from '@App/api/routes/search'
import { getOverclaimStatusCounts } from '@App/api/routes/overclaim'
import OverclaimSearchCriteriaStore from '@App/stores/overclaim/overclaim-search-criteria-store'
import { OverclaimWorkSearchQuery } from '@App/stores/search/models/overclaim-search'
import { generateSearchCriterionFromOption } from '@App/helpers/generate-search-criterion'
import { HasSearchCriteria } from '@App/stores/search/helpers/has-search-criteria'
import { DualSearchStore } from '@App/stores/search/party/dual-search-store'
import { defaultPagination } from '@App/stores/search/public/search-cowriter-store'
import { getNumTitles, INumTitles } from '@App/helpers/pagination/get-num-titles'
import { Works } from '@Entities/work/works'
import { SelectOptionHide } from '@App/stores/search/models/select-option-hide'
import { v4 as uuidv4 } from 'uuid'
import getOverclaimAutoCompleteSuggestions from '@App/helpers/search/get-overclaim-auto-complete-suggestions'
import getWriterNameAutoCompleteSuggestions from '@App/helpers/search/get-writer-name-auto-complete-suggestions'

type OverclaimStatusFilterTypes = 'OVERCLAIM' | 'DISPUTE' | 'RESOLVED' | 'MERGED' | 'CANCELED'
type DocumentFilterTypes = 'DOCUMENT_NEEDED' | 'DOCUMENT_PENDING' | 'DOCUMENT_ACCEPTED'
type AmendmentFilterTypes = 'AMENDMENT_NEEDED' | 'AMENDMENT_PENDING' | 'AMENDMENT_ACCEPTED'

export interface OverclaimFilterType {
    status?: OverclaimStatusFilterTypes[]
    sort?: 'overclaim_creation_date' | 'documentation_due_date'
    documentationStatus?: DocumentFilterTypes[]
    amendmentStatus?: AmendmentFilterTypes[]
}

const defaultOverclaimPagination = {
    page: 1,
    limit: 10,
}

export const SEARCH_OVERCLAIM_WORK_OPTIONS: Array<SelectOptionHide<OverclaimWorkSearchQuery>> = [
    {
        id: uuidv4(),
        value: 'title',
        label: 'Work Title',
        isPrimary: true,
        autoCompleteFetcher: getOverclaimAutoCompleteSuggestions,
    },
    {
        id: uuidv4(),
        value: 'writerFullNames',
        label: 'Writer Name',
        autoCompleteFetcher: getWriterNameAutoCompleteSuggestions,
        exactMatchOnly: true,
    },
    { id: uuidv4(), value: 'writerNameNumbers', label: 'Writer IPI' },
    { id: uuidv4(), value: 'mlcSongCode', label: 'MLC Song Code', isPrimary: true },
    { id: uuidv4(), value: 'overclaimHeaderId', label: 'Overclaim ID', isPrimary: true },
]
const defaultOverclaimSelectionCriteria: Array<SearchCriterion<OverclaimWorkSearchQuery>> = [
    generateSearchCriterionFromOption(SEARCH_OVERCLAIM_WORK_OPTIONS[0], true),
]

export const OVERCLAIM_SEARCH_DEFAULT_FILTERS: Partial<OverclaimFilterType> = { sort: 'overclaim_creation_date' }

export default class OverclaimSearchStore
    implements DualSearchStore<OverclaimWorkSearchQuery>, HasSearchCriteria<OverclaimWorkSearchQuery>
{
    createOverclaimCriteriaStore(): SearchCriteriaStore<OverclaimWorkSearchQuery> {
        const maxCriteria = 3
        return new OverclaimSearchCriteriaStore(
            defaultOverclaimSelectionCriteria,
            SEARCH_OVERCLAIM_WORK_OPTIONS,
            SearchOptionType.primary,
            RequiredCriteria.none,
            maxCriteria
        )
    }

    private rootStore: RootStore

    constructor(rootStore: RootStore) {
        makeObservable<
            OverclaimSearchStore,
            | '_worksRightsHolder'
            | '_worksRightsHolderPaginationInfo'
            | '_pagination'
            | '_currentSearchFilters'
            | '_overclaimStatusCounts'
            | '_showWorks'
            | '_secondarySearchPaginationInfo'
            | '_secondarySearchResults'
        >(this, {
            _worksRightsHolder: observable,
            _worksRightsHolderPaginationInfo: observable,
            _pagination: observable,
            _currentSearchFilters: observable,
            _showWorks: observable,
            _secondarySearchPaginationInfo: observable,
            _secondarySearchResults: observable,
            isDefaultPagination: computed,
            resetStore: action,
            _overclaimStatusCounts: observable,
            setCurrentSearchFilters: action,
            setCurrentPaginationLimit: action,
            setCurrentPaginationPage: action,
            changeSearchResultsLimit: action,
            setShowWorks: action,
        })
        this.rootStore = rootStore
        this.createOverclaimCriteriaStore()
    }
    private _worksRightsHolder: Works | undefined

    private _worksRightsHolderPaginationInfo: PaginationInfo | undefined

    private _secondarySearchPaginationInfo: PaginationInfo = defaultPagination

    private _pagination: PaginationBody = defaultOverclaimPagination

    private _overclaimSelectionQuery: OverclaimWorkSearchQuery | undefined

    private _currentSearchFilters: OverclaimFilterType = OVERCLAIM_SEARCH_DEFAULT_FILTERS

    private _overclaimStatusCounts: OverclaimFilterCount | undefined

    protected _currentSearchCriteriaStore: SearchCriteriaStore<OverclaimWorkSearchQuery>

    private _showWorks = false

    private _secondaryResultsPerPageLimit = defaultOverclaimPagination.limit

    private selectedGroupedOverclaims: number[]

    private _secondarySearchResults: Works | undefined

    get searchCriteriaStore(): SearchCriteriaStore<OverclaimWorkSearchQuery> {
        return this._currentSearchCriteriaStore
    }

    get pagination(): PaginationBody {
        return this._pagination
    }

    get limit(): number {
        return this._pagination.limit ?? 10
    }

    get isDefaultPagination(): boolean {
        return isEqual(this._pagination, defaultOverclaimPagination)
    }

    get worksRightsHolder(): Works | undefined {
        return this._worksRightsHolder
    }

    get showWorks(): boolean {
        return this._showWorks
    }

    get worksRightsHolderPaginationInfo(): PaginationInfo | undefined {
        return this._worksRightsHolderPaginationInfo
    }

    get secondarySearchPaginationInfo(): PaginationInfo {
        return this._secondarySearchPaginationInfo
    }

    get overclaimStatusCounts(): OverclaimFilterCount | undefined {
        return this._overclaimStatusCounts
    }

    get overclaimSearchQuery(): OverclaimWorkSearchQuery | undefined {
        return this._overclaimSelectionQuery
    }

    get filteredOverclaimSearchQuery(): OverclaimWorkSearchQuery {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { sort: _unused, ...filtersWithoutSort } = this._currentSearchFilters
        return { ...this._overclaimSelectionQuery, ...filtersWithoutSort }
    }

    get secondaryNumTitles(): INumTitles {
        return getNumTitles(
            this._secondarySearchResults?.count ?? 0,
            this._secondarySearchPaginationInfo?.currentPage ?? 1,
            this._secondaryResultsPerPageLimit
        )
    }

    get secondaryResultsPerPageLimit(): number {
        return this._secondaryResultsPerPageLimit
    }

    get secondarySearchResults(): Works | undefined {
        return this._secondarySearchResults
    }

    public setCurrentSearchFilters(filters: OverclaimFilterType) {
        this._currentSearchFilters = filters
    }

    get currentSearchFilters(): OverclaimFilterType {
        return this._currentSearchFilters
    }

    public setCurrentPaginationLimit(limit: number) {
        this._pagination.limit = limit
    }

    public setCurrentPaginationPage(page: number) {
        this._pagination.page = page
    }

    setShowWorks(showWritersWorks: boolean): void {
        this._showWorks = showWritersWorks
    }

    setOverclaimSelectionSearchQuery(overclaimSelectionQuery: OverclaimWorkSearchQuery): void {
        this._overclaimSelectionQuery = overclaimSelectionQuery
    }

    setSecondarySearchResults(works: Works) {
        this._secondarySearchResults = works
    }

    public searchForWorks = async (callBackPagination?: PaginationBody) => {
        if (callBackPagination) {
            let paginationWithSort = { ...callBackPagination }
            if (this._currentSearchFilters.sort && this._currentSearchFilters.sort.length > 0) {
                paginationWithSort = {
                    ...paginationWithSort,
                    sort: [
                        { field: this._currentSearchFilters.sort, direction: 'DESC' },
                        { field: '_id', direction: 'DESC' },
                    ],
                }
            }
            runInAction(() => {
                this._pagination = paginationWithSort
            })
        }

        await this.getWorksRightsHolder(this._pagination, this.filteredOverclaimSearchQuery)

        const page = this._pagination.page ? this._pagination.page : 1
        this.setCurrentPaginationPage(page)
    }

    async getWorksRightsHolder(pagination: PaginationBody, search?: OverclaimWorkSearchQuery): Promise<void> {
        try {
            const searchResponse = await searchOverclaimsRH(
                this.rootStore.rightsholderStore.currentRightsholderId,
                pagination,
                search
            )
            const response = {
                count: searchResponse.data.totalElements,
                works: searchResponse.data.content,
                error: undefined,
            }
            runInAction(() => {
                this._worksRightsHolder = response
                if (pagination.limit && pagination.page && response) {
                    const data = calcNumPages(response.count, pagination.limit, pagination.page)
                    if (!data) {
                        return
                    }
                    this._worksRightsHolderPaginationInfo = data
                }
            })
        } catch (e) {
            this.rootStore.popupStore.setAlertFromError(e)
        }
    }

    private searchGroupedOverclaims = async (search: OverclaimWorkSearchQuery, pagination: PaginationBody) => {
        this.rootStore.preloaderStore.openTransparentPreloader(true)
        const apiResponse = await searchOverclaimsRH(
            this.rootStore.rightsholderStore.currentRightsholderId,
            pagination,
            search
        )
        runInAction(() => {
            this._secondarySearchPaginationInfo = calcNumPages(
                apiResponse.data.totalElements,
                pagination.limit,
                pagination.page
            )
        })
        this.rootStore.preloaderStore.openTransparentPreloader(false)
        return {
            count: apiResponse.data.totalElements,
            works: apiResponse.data.content,
        }
    }

    setSelectedGroupedOverclaims(overclaimIds: number[]): void {
        this.selectedGroupedOverclaims = overclaimIds
    }

    async secondarySearch(searchOverclaimsQuery: OverclaimWorkSearchQuery) {
        this.setSelectedGroupedOverclaims(searchOverclaimsQuery.overclaimHeaderIds ?? [])
        this.searchGroupedOverclaims(
            searchOverclaimsQuery,
            this._secondarySearchPaginationInfo ?? defaultPagination
        ).then((result) => {
            runInAction(() => {
                this.setSecondarySearchResults(result)
                this.setShowWorks(true)
            })
        })
    }

    public setupOverclaimStatusCounts = async () => {
        const searchResponse = await getOverclaimStatusCounts(this.rootStore.rightsholderStore.currentRightsholderId)
        this._overclaimStatusCounts = searchResponse.data
    }

    public changeSearchResultsLimit = (newLimit: number) => {
        const newPage = findPageOnLimitChange(
            this._pagination,
            this._worksRightsHolder ? this._worksRightsHolder.count : 1,
            newLimit
        )

        const newPagination: PaginationBody = {
            page: newPage,
            limit: newLimit,
        }

        this._pagination = newPagination

        return newPagination
    }

    async secondarySetPageLimit(newLimit: number): Promise<void> {
        this._secondaryResultsPerPageLimit = newLimit
        const pagination: PaginationBody = { page: 1, limit: newLimit }
        await this.onChangeSecondarySearchPage(pagination)
    }

    private resetSecondarySearch(): void {
        this._secondarySearchResults = undefined
        this._secondarySearchPaginationInfo = defaultPagination
    }

    async onChangeSecondarySearchPage(paginationBody: PaginationBody): Promise<void> {
        if (this.selectedGroupedOverclaims) {
            this.resetSecondarySearch()
            const search: OverclaimWorkSearchQuery = {
                overclaimHeaderIds: this.selectedGroupedOverclaims,
            }

            try {
                const response = await this.searchGroupedOverclaims(search, paginationBody)

                runInAction(() => {
                    this._secondarySearchResults = response
                    if (paginationBody.limit && paginationBody.page && response) {
                        this._secondarySearchPaginationInfo = calcNumPages(
                            response.count,
                            paginationBody.limit,
                            paginationBody.page ?? 1,
                            paginationBody.refined
                        )
                    }
                })
            } catch (e) {
                this.rootStore.popupStore.setAlertFromError(e)
            }
        }
    }

    resetSearchCriteriaStore(): void {
        this._currentSearchCriteriaStore = this.createOverclaimCriteriaStore()
    }

    resetStore(): void {
        this._showWorks = false
        this._pagination = defaultOverclaimPagination
        this._overclaimSelectionQuery = undefined
        this._worksRightsHolder = undefined
        this._secondaryResultsPerPageLimit = 10
        this._secondarySearchPaginationInfo = { ...defaultPagination }
        this._worksRightsHolderPaginationInfo = undefined
        this.selectedGroupedOverclaims = []
        this._overclaimStatusCounts = undefined
        this._currentSearchFilters = OVERCLAIM_SEARCH_DEFAULT_FILTERS
        this._secondarySearchResults = undefined
        this.resetSearchCriteriaStore()
    }
}
